import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { productsPathTo } from '../utils/Api';
import { UserContext } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { ProviderProductsContext } from '../contexts/ProviderProductsContext';
import { Box, Button, Switch, TextField, Typography } from '@mui/material';
// import { Trash } from '@mui/icons-material';
import ProductPhotoPlaceholder from '../images/placeholder_product_image.png';
import FormBox from './building_blocks/FormBox';
import SnackbarContext from '../contexts/SnackbarContext';
import { TagsEdit, ProductPriceSimulation } from '.';
import { apiBaseUrl } from '../utils/Api/Api';
import CurrencyField from './CurrencyField';
import PageHeader from './building_blocks/PageHeader';
import { compressImageFile } from '../utils/compressImage';

const ProviderProduct = () => {
  const { currentUser } = useContext(UserContext);
  const { addProviderProduct, updateProviderProduct, removeProviderProduct, getTags } = useContext(ProviderProductsContext);
  const { openSnackbar, openConfirmDialog } = useContext(SnackbarContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [photoPreview, setPhotoPreview] = useState();
  const [product, setProduct] = useState({
    id: '',
    title: '',
    description: '',
    price: 0,
    published: false,
    quantity: 5,
    provider_id: currentUser.provider_id,
  });

  useEffect(() => {
    if (id) {
      productsPathTo.get(id).then(result => {
        setProduct(result.data);
      });
    }
  }, [id]);

  useEffect(() => {
    document.title = 'Produto do fornecedor - TudoPets.app';
  }, []);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setProduct({ ...product, [name]: type === 'checkbox' ? checked : value });
  };

  const productErrorMessage = (error) => {
    const data = error?.response?.data;
    if (Array.isArray(data?.errors)) {
      return data.errors.join(', ');
    }
    const errorBag = data?.errors && typeof data.errors === 'object' ? data.errors : data;
    if (errorBag && typeof errorBag === 'object') {
      const messages = Object.values(errorBag).flat().filter(Boolean);
      if (messages.length) {
        return messages.join(', ');
      }
    }
    return id ? 'Não foi possível atualizar o produto. Tente novamente.' : 'Não foi possível criar o produto. Tente novamente.';
  };

  const productPayload = () => {
    const payload = {
      title: product.title,
      description: product.description,
      price: product.price,
      quantity: product.quantity || 0,
      published: product.published,
      tags: (product.tags || []).map((tag) => ({ id: tag.id })),
    };

    if (typeof product.item_photo === 'string' && product.item_photo.startsWith('data:')) {
      payload.item_photo = product.item_photo;
    }

    return payload;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = productPayload();
    const request = id
      ? productsPathTo.update(id, payload, getTags(payload.tags))
      : productsPathTo.save(payload, getTags(payload.tags));

    request.then((result) => {
      if (id) {
        updateProviderProduct(result.data);
        openSnackbar("Produto atualizado com sucesso!", "success");
      } else {
        addProviderProduct(result.data);
        openSnackbar("Produto criado com sucesso!", "success");
      }
      navigate(`/provider-dashboard`);
    }).catch((error) => {
      console.error('Error saving product:', error);
      openSnackbar(productErrorMessage(error), "error");
    });
  };

  const handleDelete = () => {
    openConfirmDialog(
      'Você tem certeza que deseja remover este produto? Esta ação é irreversível',
      async () => {
        try {
          await productsPathTo.delete(product.id);
          removeProviderProduct(product.id);
          openSnackbar("Produto removido com sucesso!", "success");
          navigate(`/provider-dashboard`);
        } catch (error) {
          console.error('Error deleting product:', error);
        }
      }
    );
  };

  const handleImageChange = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;

    try {
      const dataUrl = await compressImageFile(file);
      setProduct({ ...product, item_photo: dataUrl });
      setPhotoPreview(dataUrl);
    } catch (error) {
      console.error('Product photo upload failed:', error);
      openSnackbar("Não foi possível processar a foto. Tente outra imagem.", "error");
    } finally {
      input.value = '';
    }
  };

  if (!product) {
    return <div>Carregando...</div>;
  }

  return (
    <FormBox>
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="baseline">
        <PageHeader
          title={id ? 'Editar produto' : 'Novo produto'}
          onBack={() => navigate('/provider-dashboard')}
        />
        <Box style={{width: '100%'}} >
          <form onSubmit={handleSubmit}>
            <Typography mt={2} mb={3} textAlign="left" variant="body2"><b>Dados principais</b></Typography>
            <Box>
              <Box mt={2}>
                <TextField
                  name="title"
                  label="Título"
                  variant="outlined"
                  type="text"
                  value={product.title}
                  onChange={handleInputChange}
                  autoComplete="off"
                  fullWidth
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                    },
                  }}
                  disabled={product.been_published === true}
                />
                {product.been_published === true && (<Typography variant="body2">Este produto foi publicado na loja. Título não pode ser alterado.</Typography>)}
              </Box>
              <Box mt={2}>
                <TextField name="description" autoComplete="off" label="Descrição" variant="outlined" type="text" value={product.description} multiline onChange={handleInputChange} fullWidth sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                  },
                }}/>
              </Box>
              <Box mt={2}>
                <TextField name="quantity" label="Quantidade" variant="outlined" type="number" value={product.quantity} inputProps={{ min: 0 }} onChange={handleInputChange} fullWidth sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                  },
                }}/>
                <Typography component="small" variant="body2">Quantidade máxima permitida no carrinho. (0 = Sem limite)</Typography>
              </Box>
              <Box mt={2}>
                <TagsEdit resource={product} setResource={setProduct} />
              </Box>
              <Box mt={2}>
            </Box>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="raised-button-file"
              type="file"
              onChange={handleImageChange}
              />
            <Typography mt={2} mb={3} textAlign="left" variant="body2"><b>Foto do produto</b></Typography>
            <Box>
              <label htmlFor="raised-button-file">
                <Button variant="outlined" component="span">
                {product.item_photo ? 'Atualizar' : 'Adicionar'} foto
                </Button>
              </label>
              { photoPreview && (
                  <img style={{ width: '100%', height: '150px', objectFit: 'scale-down', marginTop: '12px' }} src={photoPreview} />
                )
              }
              { !photoPreview && (
                <img style={{ width: '100%', height: '150px', objectFit: 'scale-down', marginTop: '12px' }} src={id && product.item_photo ? `${apiBaseUrl}${product.item_photo}` : ProductPhotoPlaceholder} />
              )}
            </Box>
            <Typography mt={2} mb={3} textAlign="left" variant="body2"><b>Precificação</b></Typography>
            <Box>
              <Box mt={2}>
                <CurrencyField
                  value={product.price}
                  onChange={(price) => setProduct({ ...product, price: price })}
                />
                <ProductPriceSimulation product={product} />
              </Box>
            </Box>
            <Box display="flex" mt={4}>
              {id && (
                <Button variant="outlined" color="error" onClick={handleDelete}>Remover Produto</Button>
              )}
              <Button sx={{marginLeft: 'auto'}} variant="contained" type="submit">{id ? 'Atualizar' : 'Criar'}</Button>
            </Box>
          </Box>
          </form>
        </Box>
      </Box>
    </FormBox>
  );
};

export default ProviderProduct
